import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormRef } from 'devextreme-react/form';
import './image-gallery.css';

type ImageGalleryPreviewAlignment = 'left' | 'center' | 'right';

type IUseImageGalleryInputParametres<T> = {
  champImage: keyof T & string,
  champNomImage?: keyof T & string,
  dataRow: T | undefined,
  setDataRow: Dispatch<SetStateAction<T>>,
  setIsFieldModifier: (isFieldModifier: boolean) => void,
  isModeModifier: boolean,
  formRef?: RefObject<FormRef | null>,
  selectButtonText?: string,
  removeButtonText?: string,
  noDataText?: string,
  maxImageHeight?: number,
  maxImageWidth?: number,
  showPreviewBackground?: boolean,
  previewAlignment?: ImageGalleryPreviewAlignment,
}

function useImageGalleryInput<T>({
  champImage,
  champNomImage,
  dataRow,
  setDataRow,
  setIsFieldModifier,
  isModeModifier,
  formRef,
  selectButtonText = 'Changer…',
  removeButtonText = 'Enlever',
  noDataText = 'Pas de données image',
  maxImageHeight = 300,
  maxImageWidth = 300,
  showPreviewBackground = true,
  previewAlignment = 'center',
}: IUseImageGalleryInputParametres<T>) {
  const fileUploaderRef = useRef<any>(null);
  const [photoSrc, setPhotoSrc] = useState('');

  const imageValue = dataRow?.[champImage] as string | undefined;

  useEffect(() => {
    if (imageValue) {
      setPhotoSrc(
        imageValue.startsWith('data:')
          ? imageValue
          : `data:image/jpeg;base64,${imageValue}`
      );
    } else {
      setPhotoSrc('');
    }
  }, [dataRow, imageValue]);

  const onImageSelected = useCallback((files: File[]) => {
    const file = files?.[0];
    if (!file) {
      alert('File not selected or browser incompatible.');
      return;
    }

    const fr = new FileReader();
    fr.onload = () => {
      if (!fr.result) {
        return;
      }

      const dataUrl = fr.result.toString();
      const base64 = dataUrl.split(',')[1] || '';
      setPhotoSrc(dataUrl);
      setDataRow((prev: T) => ({
        ...prev,
        [champImage]: base64,
        ...(champNomImage ? { [champNomImage]: file.name } : {}),
      }));
      setIsFieldModifier(true);

      if (champNomImage) {
        formRef?.current?.instance().updateData(champNomImage, file.name);
      }
    };
    fr.readAsDataURL(file);
  }, [champImage, champNomImage, formRef, setDataRow, setIsFieldModifier]);

  const onImageRemoved = useCallback(() => {
    setPhotoSrc('');
    setDataRow((prev: T) => ({
      ...prev,
      [champImage]: '',
      ...(champNomImage ? { [champNomImage]: '' } : {}),
    }));
    setIsFieldModifier(true);

    if (champNomImage) {
      formRef?.current?.instance().updateData(champNomImage, '');
    }

    fileUploaderRef.current?.instance?.reset?.();
  }, [champImage, champNomImage, formRef, setDataRow, setIsFieldModifier]);

  const fileUploaderOptions = useMemo(() => ({
    disabled: !isModeModifier,
    onValueChange: onImageSelected,
    showFileList: false,
    selectButtonText,
    labelText: '',
    accept: 'image/*',
    uploadMode: 'useForm' as const,
  }), [isModeModifier, onImageSelected, selectButtonText]);

  const removeButtonOptions = useMemo(() => ({
    disabled: !isModeModifier,
    onClick: onImageRemoved,
    text: removeButtonText,
  }), [isModeModifier, onImageRemoved, removeButtonText]);

  return {
    photoSrc,
    fileUploaderRef,
    fileUploaderOptions,
    removeButtonOptions,
    noDataText,
    maxImageHeight,
    maxImageWidth,
    showPreviewBackground,
    previewAlignment,
  };
}

const getPreviewAlignmentClassName = (alignment: ImageGalleryPreviewAlignment) => {
  switch (alignment) {
    case 'left':
      return 'galerie-image-apercu--align-left';
    case 'right':
      return 'galerie-image-apercu--align-right';
    default:
      return 'galerie-image-apercu--align-center';
  }
};

type ImageGalleryPreviewProps = {
  photoSrc: string,
  noDataText?: string,
  maxHeight?: number,
  maxWidth?: number,
  showBackground?: boolean,
  previewAlignment?: ImageGalleryPreviewAlignment,
}

export function ImageGalleryPreview({
  photoSrc,
  noDataText = 'Pas de données image',
  maxHeight = 300,
  maxWidth = 300,
  showBackground = true,
  previewAlignment = 'center',
}: ImageGalleryPreviewProps) {
  const previewClassName = [
    'galerie-image-apercu',
    getPreviewAlignmentClassName(previewAlignment),
    !photoSrc ? 'galerie-image-apercu--vide' : '',
    !showBackground ? 'galerie-image-apercu--sans-fond' : '',
  ].filter(Boolean).join(' ');

  if (!photoSrc) {
    return (
      <div className={previewClassName}>
        {noDataText}
      </div>
    );
  }

  return (
    <div className={previewClassName}>
      <img
        src={photoSrc}
        alt=""
        className="galerie-image-apercu__image"
        style={{ maxHeight, maxWidth }}
      />
    </div>
  );
}

export default useImageGalleryInput;
